import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/constants";
import { fmtDate } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, ChevronRight, ChevronDown, Paperclip, ExternalLink } from "lucide-react";
import FilePreviewSheet from "@/components/files/FilePreviewSheet";
import { FEE_PAID_BY_META, FEE_STATUS_META, reimbursementBadge } from "@/components/fees/feeUtils";

const money = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
const FEE_STATUSES = [["pending", "Pending"], ["invoiced", "Invoiced"], ["paid", "Paid"]];
const INVOICE_TARGETS = [["client", "Client"], ["builder", "Builder"], ["custom", "Custom"]];

export default function FeesReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, outstanding: 0, gst: 0, count: 0, projects_with_fees: 0 });
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [paidBy, setPaidBy] = useState("all");
  const [invoicedTo, setInvoicedTo] = useState("all");
  const [builders, setBuilders] = useState([]);
  const [months, setMonths] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null);

  const toggle = (pid) => setExpanded((e) => ({ ...e, [pid]: !e[pid] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "all") params.category = category;
      if (status !== "all") params.status = status;
      if (paidBy !== "all") params.paid_by = paidBy;
      if (invoicedTo !== "all") params.invoiced_to = invoicedTo;
      const { data } = await api.get("/fees/report", { params });
      setRows(data.projects || []);
      setBuilders(data.builders || []);
      setMonths(data.by_month || []);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error(apiErr(e, "Could not load the fees report"));
    } finally { setLoading(false); }
  }, [category, status, paidBy, invoicedTo]);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { k: "total", label: "Total billed (incl. GST)", val: summary.total, accent: "text-zinc-900" },
    { k: "paid", label: "Paid", val: summary.paid, accent: "text-emerald-600" },
    { k: "outstanding", label: "Outstanding", val: summary.outstanding, accent: "text-amber-600" },
    { k: "gst", label: "GST component", val: summary.gst, accent: "text-zinc-500" },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-[1200px]" data-testid="fees-report">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-1.5">
          <Receipt className="h-3.5 w-3.5" /> Admin · Finance
        </p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">Fees Report</h1>
        <p className="text-zinc-500 mt-2 text-sm">Outstanding vs paid fees across every project in the studio.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="fees-report-filters">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[220px] rounded-sm" data-testid="fees-report-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORY_META[c].label} · {CATEGORY_META[c].sub}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] rounded-sm" data-testid="fees-report-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {FEE_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={paidBy} onValueChange={setPaidBy}>
          <SelectTrigger className="w-[150px] rounded-sm" data-testid="fees-report-filter-paidby"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Paid by · any</SelectItem>
            <SelectItem value="client">Client paid</SelectItem>
            <SelectItem value="twp">TWP paid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={invoicedTo} onValueChange={setInvoicedTo}>
          <SelectTrigger className="w-[160px] rounded-sm" data-testid="fees-report-filter-invoicedto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Invoiced to · any</SelectItem>
            {INVOICE_TARGETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {(category !== "all" || status !== "all" || paidBy !== "all" || invoicedTo !== "all") && (
          <button onClick={() => { setCategory("all"); setStatus("all"); setPaidBy("all"); setInvoicedTo("all"); }} className="text-xs px-3 py-1.5 rounded-full border border-zinc-300 text-zinc-600 hover:bg-zinc-100 transition-colors" data-testid="fees-report-reset">Reset</button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {cards.map((c) => (
          <div key={c.k} className="border border-zinc-200 bg-white p-4" data-testid={`fees-report-${c.k}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{c.label}</p>
            <p className={`font-heading text-2xl font-black tracking-tight mt-1 ${c.accent}`}>{money.format(c.val || 0)}</p>
          </div>
        ))}
      </div>

      {summary.overdue_amount > 0 && (
        <div className="border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 mb-6 flex items-center gap-2 text-sm" data-testid="fees-report-overdue-banner">
          <span className="font-semibold">{money.format(summary.overdue_amount)}</span> overdue across {summary.overdue_count} invoiced fee{summary.overdue_count !== 1 ? "s" : ""} past their due date — worth chasing.
        </div>
      )}

      {summary.awaiting_reimbursement > 0 && (
        <div className="border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 mb-6 flex items-center gap-2 text-sm" data-testid="fees-report-reimbursement-banner">
          <span className="font-semibold">{money.format(summary.awaiting_reimbursement)}</span> paid by TWP and recoverable from clients — not yet reimbursed.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Cash-flow: paid fees by month */}
        <div className="border border-zinc-200 bg-white p-4" data-testid="fees-report-cashflow">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4">Cash-flow · paid per month</p>
          {months.length === 0 ? (
            <p className="text-sm text-zinc-400">No payments recorded yet.</p>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {months.slice(-12).map((m) => {
                const max = Math.max(...months.map((x) => x.paid), 1);
                const h = Math.max(4, Math.round((m.paid / max) * 130));
                const [y, mo] = m.month.split("-");
                const label = new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-AU", { month: "short" });
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.month}: ${money.format(m.paid)}`}>
                    <span className="text-[9px] text-zinc-500 tabular-nums">{money.format(m.paid).replace(/\.00$/, "")}</span>
                    <div className="w-full bg-emerald-500 rounded-t-sm" style={{ height: `${h}px` }} />
                    <span className="text-[10px] text-zinc-400">{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Builder rollup */}
        <div className="border border-zinc-200 bg-white" data-testid="fees-report-builders">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 px-4 py-3 border-b border-zinc-200">By builder · who owes the most</p>
          <div className="max-h-40 overflow-y-auto">
            {builders.length === 0 ? (
              <p className="p-4 text-sm text-zinc-400">No fees yet.</p>
            ) : builders.map((b) => (
              <div key={b.builder} className="px-4 py-2.5 border-b border-zinc-100 last:border-0 grid grid-cols-[minmax(0,1fr)_120px] gap-3 items-center" data-testid={`fees-builder-${b.builder}`}>
                <span className="text-sm truncate">{b.builder}</span>
                <span className={`text-sm font-semibold tabular-nums text-right ${b.outstanding > 0 ? "text-amber-600" : "text-zinc-400"}`}>{money.format(b.outstanding || 0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div className="min-w-[600px]">
        <div className="px-4 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 grid grid-cols-[minmax(0,1fr)_130px_130px_140px_30px] gap-3">
          <span>Project</span><span>Total</span><span>Paid</span><span>Outstanding</span><span></span>
        </div>
        {loading ? (
          <div className="p-8"><div className="h-6 w-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-400">No fees recorded on any project yet.</p>
        ) : rows.map((r) => {
          const meta = CATEGORY_META[r.category];
          const isOpen = !!expanded[r.project_id];
          return (
            <div key={r.project_id} className="border-b border-zinc-100 last:border-0" data-testid={`fees-report-row-${r.project_id}`}>
              <button onClick={() => toggle(r.project_id)}
                className="w-full text-left px-4 py-3 grid grid-cols-[minmax(0,1fr)_130px_130px_140px_30px] gap-3 items-center hover:bg-zinc-50 transition-colors"
                data-testid={`fees-report-toggle-${r.project_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.project_name}</p>
                  <p className="text-xs text-zinc-400 truncate">
                    {r.builder ? `${r.builder} · ` : ""}{meta ? `${meta.label} · ${meta.sub}` : ""} · {r.count} fee{r.count !== 1 ? "s" : ""}
                  </p>
                  {r.overdue > 0 && (
                    <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-rose-100 text-rose-700 border border-rose-200" data-testid={`fees-report-overdue-${r.project_id}`}>{money.format(r.overdue)} overdue</span>
                  )}
                </div>
                <span className="text-sm font-semibold tabular-nums">{money.format(r.total || 0)}</span>
                <span className="text-sm tabular-nums text-emerald-600">{money.format(r.paid || 0)}</span>
                <span className={`text-sm font-semibold tabular-nums ${r.outstanding > 0 ? "text-amber-600" : "text-zinc-400"}`}>{money.format(r.outstanding || 0)}</span>
                {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400 justify-self-end" /> : <ChevronRight className="h-4 w-4 text-zinc-300 justify-self-end" />}
              </button>

              {isOpen && (
                <div className="bg-zinc-50/70 border-t border-zinc-100 px-4 py-3 space-y-2" data-testid={`fees-report-detail-${r.project_id}`}>
                  {(r.fees || []).map((f) => {
                    const paidBy = FEE_PAID_BY_META[f.paid_by];
                    const st = FEE_STATUS_META[f.status];
                    const reimb = reimbursementBadge(f);
                    return (
                      <div key={f.fee_id} className="bg-white border border-zinc-200 rounded-sm px-3 py-2" data-testid={`fees-report-fee-${f.fee_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{f.label}</p>
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${st?.cls || ""}`}>{st?.label || f.status}</span>
                              {paidBy && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${paidBy.cls}`}>{paidBy.label}</span>}
                              {f.invoiced_to_display && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border bg-zinc-100 text-zinc-600 border-zinc-200 truncate max-w-[180px]">Bill → {f.invoiced_to_display}</span>}
                              {reimb && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border ${reimb.cls}`}>{reimb.label}{reimb.label === "Reimbursed" && f.reimbursed_date ? ` ${fmtDate(f.reimbursed_date)}` : ""}</span>}
                              {f.overdue && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border bg-rose-100 text-rose-700 border-rose-200">Overdue</span>}
                            </div>
                            {(f.payment_method || f.payment_reference) && (
                              <p className="text-[11px] text-zinc-400 mt-1 truncate">
                                {[f.payment_method, f.payment_reference && `ref ${f.payment_reference}`].filter(Boolean).join(" · ")}
                              </p>
                            )}
                            {(f.attachments || []).length > 0 && (
                              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                {f.attachments.map((a) => (
                                  <button key={a.file_id} onClick={() => setPreview(a)} title={a.original_filename}
                                    className="inline-flex items-center gap-1 text-[10px] text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-sm px-1.5 py-0.5 max-w-[170px] bg-white"
                                    data-testid={`fees-report-attachment-${a.file_id}`}>
                                    <Paperclip className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{a.label || a.original_filename}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-semibold tabular-nums shrink-0">{money.format(f.total || 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => navigate(`/project/${r.project_id}`)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900 transition-colors mt-1"
                    data-testid={`fees-report-open-${r.project_id}`}>
                    Open project <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
      <FilePreviewSheet file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
